import React, { useState, useRef } from "react";
import {
    Box, Button, TextField, IconButton, Typography, Paper, Alert, Collapse
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearIcon from "@mui/icons-material/Clear";
import UploadIcon from "@mui/icons-material/Upload";
import * as XLSX from "xlsx";
import ConfirmDialog from "../../Common/ConfirmDialog";

/**
 * Reusable data input panel used by the MicroSlide Response, Calibration
 * and Prediction tabs. Mirrors MicroTip/CalibrationData.js but is parameterised
 * by `columns` so each tab can use its own schema.
 *
 * Props:
 *   columns: [{ key, label }]   one TextField per column per row
 *   rows, setRows
 *   placeholder: optional textarea placeholder
 */
export default function DataInput({ columns, rows, setRows, placeholder }) {
    const [textArea, setTextArea] = useState("");
    const [errors, setErrors] = useState([]);
    const [showError, setShowError] = useState(true);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const blankRow = () => Object.fromEntries(columns.map(c => [c.key, ""]));

    const addRow = () => setRows([...rows, blankRow()]);
    const removeRow = (i) => {
        const next = [...rows];
        next.splice(i, 1);
        setRows(next.length ? next : [blankRow()]);
    };
    const handleRowChange = (i, key, value) => {
        const next = [...rows];
        next[i] = { ...next[i], [key]: value };
        setRows(next);
    };
    const resetGrid = () => setRows([blankRow()]);
    const clearTextArea = () => {
        setTextArea("");
        setErrors([]);
        setShowError(false);
        resetGrid();
    };

    const handleParseText = (content) => {
        setErrors([]);
        setShowError(true);

        const text = content !== undefined ? content : textArea;
        if (!text.trim()) {
            setErrors(["Values are empty. Please enter or upload data."]);
            return;
        }

        const lines = text.split("\n").filter(l => l.trim() !== "");
        const tempErrors = [];
        const parsed = [];

        lines.forEach((line, idx) => {
            // skip header line if any column label appears
            const lower = line.toLowerCase();
            if (columns.some(c => lower.includes(c.key.toLowerCase()) || lower.includes(c.label.toLowerCase()))) {
                if (idx === 0) return;
            }

            const cols = line.trim().split(/[\t ,]+/);
            if (cols.length < 1) return;

            const row = blankRow();
            columns.forEach((c, i) => {
                row[c.key] = cols[i] !== undefined ? cols[i] : "";
            });
            parsed.push(row);
        });

        setErrors(tempErrors);
        if (parsed.length > 0) setRows(parsed);
    };

    const handleFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            let content;
            const ext = file.name.split(".").pop().toLowerCase();
            if (ext === "csv" || ext === "txt") {
                content = e.target.result;
            } else if (ext === "xlsx" || ext === "xls") {
                const data = new Uint8Array(e.target.result);
                const wb = XLSX.read(data, { type: "array" });
                content = XLSX.utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]]);
            } else {
                alert("Unsupported file type. Please use TXT, CSV or XLSX.");
                return;
            }
            setTextArea(content);
            setRows([blankRow()]);
            setErrors([]);
            setShowError(true);
            handleParseText(content);
        };
        if (file.name.match(/\.(xls|xlsx)$/i)) reader.readAsArrayBuffer(file);
        else reader.readAsText(file);
    };

    const handleTextAreaChange = (e) => {
        setTextArea(e.target.value);
        setErrors([]);
        setShowError(false);
    };

    return (
        <Paper elevation={0} sx={{ margin: "auto", border: "none" }}>
            <Box
                sx={{
                    borderColor: "grey.300",
                    borderRadius: 1,
                    maxHeight: 220,
                    overflow: "auto",
                    mb: 2,
                    position: "relative",
                    p: 1,
                    border: dragActive ? "1px dashed #1976d2" : "0px",
                    backgroundColor: dragActive ? "rgba(25,118,210,0.05)" : "transparent"
                }}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
                }}
            >
                <TextField
                    multiline
                    minRows={5}
                    maxRows={20}
                    value={textArea}
                    onChange={handleTextAreaChange}
                    variant="outlined"
                    fullWidth
                    placeholder={placeholder}
                />

                {!textArea && (
                    <Box>
                        <Typography
                            variant="subtitle1"
                            align="center"
                            sx={{
                                position: "absolute", top: "30%", left: "50%",
                                transform: "translate(-50%, -50%)", zIndex: 2
                            }}
                        >
                            Drag &amp; drop, paste or enter values.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                position: "absolute", top: "50%", left: "50%",
                                transform: "translate(-50%, -50%)", zIndex: 2
                            }}
                        >
                            Upload File
                        </Button>
                    </Box>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,.txt"
                    style={{ display: "none" }}
                    onChange={(e) => {
                        if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
                    }}
                />
            </Box>

            {errors.length > 0 && (
                <Collapse in={showError} sx={{ mb: 2 }}>
                    <Alert
                        severity="error"
                        action={
                            <IconButton size="small" color="inherit" onClick={() => setShowError(false)}>
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {errors.map((er, i) => <div key={i}>{er}</div>)}
                    </Alert>
                </Collapse>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" gap={1}>
                    <Button variant="contained" startIcon={<UploadIcon />} onClick={() => handleParseText()}>
                        Parse Values
                    </Button>
                    <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearTextArea}>
                        Clear Values
                    </Button>
                </Box>

                <Box display="flex" gap={1}>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addRow}>
                        Add Row
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={() => setOpenResetDialog(true)}
                    >
                        Reset
                    </Button>
                    <ConfirmDialog
                        open={openResetDialog}
                        title="Reset Data"
                        message="Are you sure you want to clear the data?"
                        onCancel={() => setOpenResetDialog(false)}
                        onConfirm={() => { resetGrid(); setOpenResetDialog(false); }}
                    />
                </Box>
            </Box>

            <Box border={1} borderColor="grey.300" borderRadius={1} overflow="auto" sx={{ p: 1 }}>
                <Box display="flex" gap={1} mb={1}>
                    {columns.map(c => (
                        <Typography key={c.key} sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>
                            {c.label}
                        </Typography>
                    ))}
                    <Box width={40} />
                </Box>

                {rows.map((row, i) => (
                    <Box key={i} display="flex" gap={1} alignItems="center" mb={1}>
                        {columns.map(c => (
                            <TextField
                                key={c.key}
                                size="small"
                                sx={{ flex: 1 }}
                                value={row[c.key] ?? ""}
                                onChange={(e) => handleRowChange(i, c.key, e.target.value)}
                            />
                        ))}
                        <IconButton size="small" onClick={() => removeRow(i)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}
